import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import Skeleton from "@mui/material/Skeleton";
import React, { useEffect, useState } from "react";
import ListItemText from "@mui/material/ListItemText";
import { useSelector, useDispatch } from "react-redux"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { hasValidAdvisor } from "../BusinessCards.js";
import { postRequest, showMessage } from "../../../../config.js";
import "react-circular-progressbar/dist/styles.css";
import { refreshCompanies, setCompanies } from "../../../../reducers/companiesReducer.js";

export default function PermissionsClient({ profileClass, advisors, getAuditLogs, setRefresh, sectionsWithChecklist, company, loading }) {
  let user = useSelector((state) => state.user);
  let companies = useSelector((state) => state.companies);
  const dispatch = useDispatch()
  const [allCompanies, setAllCompanies] = useState([]);

  useEffect(() => {
    if (companies && companies.length > 0) {
      const _companies = companies.reduce((acc, company) => {
        const seen = new Set(acc.map(c => c._id));

        // Clone parent company and delete childrenCompanies
        const companyCopy = { ...company };
        delete companyCopy.childrenCompanies;

        // if (company.progress !== 'inActive' && !seen.has(company._id)) {
        // Include all companies (including Free/inactive) in permissions table
        if (!seen.has(company._id)) {
          acc.push(companyCopy);
          seen.add(company._id);
        }

        if (Array.isArray(company.childrenCompanies)) {
          company.childrenCompanies.forEach(child => {
            const childCopy = { ...child };
            delete childCopy.childrenCompanies;

            // if (child.progress !== 'inActive' && !seen.has(child._id)) {
            // Include all children companies (including Free/inactive) in permissions table
            if (!seen.has(child._id)) {
              acc.push(childCopy);
              seen.add(child._id);
            }
          });
        }

        return acc;
      }, []);

      setAllCompanies(_companies);
    }
  }, [companies]);


  let [sections, setSections] = useState([]);

  let [groupedSections, setGroupedSections] = useState(null)

  useEffect(() => {
    const extraModules = company['extraModules'] && company['extraModules'].length ? company['extraModules'].map((mod) => ({
      type: mod.type,
      _id: mod._id,
      sectionNo: mod.id, // or mod.moduleKey if you prefer that
      sectionName: mod.moduleName
    })) : [];

    let generateChecklistModuleRawData = [];
    allCompanies.forEach((company) => {
      if (company.generateChecklistModules) {
        generateChecklistModuleRawData.push(...company.generateChecklistModules);
      }
    });

    const generateChecklistModules = generateChecklistModuleRawData && generateChecklistModuleRawData.length ? generateChecklistModuleRawData.map((mod) => {
      return {
        checklistModule: true,
        type: mod.type,
        _id: mod._id,
        sectionNo: mod.moduleKey,
        sectionName: mod.moduleName
      }
    }) : [];


    // Combine all sections and filter out duplicates by sectionNo
    const allSections = [...JSON.parse(JSON.stringify(sectionsWithChecklist)), ...extraModules, ...generateChecklistModules];
    const seenSectionNos = new Set();
    let sectionObj = allSections.filter(section => {
      if (seenSectionNos.has(section.sectionNo)) {
        return false; // Skip duplicate
      }
      seenSectionNos.add(section.sectionNo);
      return true;
    });
    let userType = user["role"] === "Advisor" ? "advisors" : "investors";

    for (let company of allCompanies) {
      if (company[userType]) {
        for (let advisorId of Object.keys(company[userType])) {
          let advisorSettings = company[userType][advisorId];
          let advisor = advisors.find(({ _id }) => _id == advisorId);

          // Skip if no modules or advisor found
          if (!advisorSettings["modules"] || !advisor) continue;

          for (let [key, value] of Object.entries(advisorSettings["modules"])) {
            if (!value) continue;
            let sectionIndex = sectionObj.findIndex((section) => {
              if (section.companyId) {
                return section.sectionNo === key && company["_id"] === section.companyId;
              }
              return section.sectionNo === key;
            });

            if (sectionObj && sectionIndex >= 0) {
              // Ensure the permission key exists and structure it correctly
              if (!sectionObj?.[sectionIndex]?.["permission"]) {
                sectionObj[sectionIndex]["permission"] = {};  // Initialize if not exists
              }

              if (!sectionObj[sectionIndex]["permission"][company["_id"]]) {
                sectionObj[sectionIndex]["permission"][company["_id"]] = {};  // Initialize company permission if not exists
              }

              // Now, set the permission data for the specific advisor
              sectionObj[sectionIndex]["permission"][company["_id"]][advisorId] = advisor;
            }
          }
        }
      }
    }
    setSections(sectionObj);
  }, [allCompanies, sectionsWithChecklist]);


  useEffect(() => {
    if (sections && sections.length) {
      const _groupedSections = sections.reduce((acc, section) => {
        let type = section.sectionNo.includes("UD0")
          ? "Custom Section"
          : section.type && /extensions pack|extentions pack/i.test(section.type)
            ? "Expension Pack"
            : section.type && section.type.includes("-")
              ? section.type.split("-")[0].trim()
              : section.type;

        type = type?.trim();
        const allowedTypes = [
          "Existing Sections",
          "Expension Pack",
          "Additional Sections",
          "Custom Section"
        ];

        if (!allowedTypes.includes(type)) {
          if (section?.checklistModule) {
            type = 'checklist'
          } else {
            type = "extraModules";
          }
        }

        if (!acc[type]) acc[type] = [];
        acc[type].push(section);
        return acc;
      }, {});
      setGroupedSections(_groupedSections);
    }
  }, [sections])

  const handlePermissions = async (e, advisor, sectionNo, company) => {
    const status = e["target"]["checked"]
    const res = await postRequest(`/user/advisorPermissions/${company["_id"]}`, {
      advisor,
      company,
      advisorId: advisor["_id"],
      sectionNo,
      status,
      firmId: user["companyId"]["_id"],
    }
    );
    let userType = advisor["role"] == "Advisor" ? "advisors" : "investors";
    if (res && res["code"] == 200) {
      let sectionObj = JSON.parse(JSON.stringify(sections));
      let sectionIndex = sections.findIndex((section) => section["sectionNo"] == sectionNo);
      const companyId = company["_id"];
      const advisorId = advisor["_id"];
      if (status) {
        if (!sectionObj[sectionIndex]["permission"] || !sectionObj[sectionIndex]["permission"][companyId]) {
          sectionObj[sectionIndex] = {
            ...sectionObj[sectionIndex],
            "permission": {
              [companyId]: null
            }
          }
        }
        sectionObj[sectionIndex]["permission"][companyId] = {
          ...sectionObj[sectionIndex]["permission"][companyId],
          [advisorId]: {
            ...advisor,
            permissions: {
              ...((company[userType][advisorId] && company[userType][advisorId]['modules']) && company[userType][advisorId]['modules']),
              [sectionNo]: status
            }
          }
        };
      } else {
        delete sectionObj[sectionIndex]["permission"]?.[company["_id"]]?.[advisor["_id"]];
      }

      const updatedData = allCompanies.map((item) => {
        if (item._id === companyId) {
          return {
            ...item,
            [userType]: {
              ...item[userType],
              [advisorId]: {
                ...item[userType][advisorId],
                modules: {
                  ...item?.[userType]?.[advisorId]?.modules,
                  [sectionNo]: status
                }
              }
            }
          };
        }
        return item; // Return other items unchanged
      });
      dispatch(setCompanies(updatedData))
      setSections(sectionObj);
      showMessage(res["message"]);
      setRefresh((r) => !r);
    }
  };

  // on advisor multi allocation
  const handleMultiPermissions = async (e, advisor, sectionNo, company, sections) => {

    const generated = company?.['generateChecklistModules']?.map((mod) => mod.moduleKey) || [];
    const _checked = e["target"]["checked"];
    const result = await postRequest(`/user/advisorMultiPermissions/${company["_id"]}`, {
      advisor,
      company,
      advisorId: advisor["_id"],
      sectionNo: [...sectionNo, ...(generated || [])],
      status: _checked,
      firmId: user["companyId"]["_id"],
    });

    if (result && result["code"] == 200) {
      let sectionObj = await JSON.parse(JSON.stringify(sections));
      sections.map((_, sectionIndex) => {
        const advisorId = advisor["_id"]
        if (_checked === true) {
          if (sectionObj[sectionIndex]["permission"]?.[company["_id"]]) {
            if (!sectionObj[sectionIndex]["permission"][company["_id"]][advisor["_id"]]) {
              sectionObj[sectionIndex]["permission"][company["_id"]] = {
                ...sectionObj[sectionIndex]["permission"]?.[company["_id"]],
                [advisor["_id"]]: {
                  ...advisor,
                  permissions: {
                    ...company['advisors'][advisorId]['modules'],
                    [sectionNo]: _checked
                  }
                },
              };
            }
          } else {
            sectionObj[sectionIndex] = {
              ...sectionObj[sectionIndex],
              permission: {
                [company["_id"]]: {
                  [advisor["_id"]]: advisor
                }
              }
            }
          }
        } else {
          delete sectionObj[sectionIndex]['permission'][company["_id"]][advisor["_id"]]
        }
      })
      setSections(sectionObj);
    }
    showMessage(result["message"]);
    setRefresh((r) => !r)
  };

  function arraysMatch(data, allList) {
    const allPermissionsExist = data.every(permission => allList.includes(permission));
    return allPermissionsExist
  }

  const checkAdvisorHasAllAllocations = ({ advisor, company }) => {
    if (advisor && company?.['advisors']?.[`${advisor['_id']}`]?.['modules']) {
      const allAllocationCode = sectionsWithChecklist.map((item) => item.sectionNo)
      const advisorPermission = []
      Object.entries(company['advisors'][`${advisor['_id']}`]['modules']).map((item) => item[1] === true && advisorPermission.push(item[0]))
      const result = arraysMatch(allAllocationCode, advisorPermission)
      return result
    }
    return false
  }

  function capitalizeFirstLetter(val) {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
  }
  const getSectionTitle = (type) => {
    if (type === "existing sections") {
      return "Core Modules";
    } else if (type === "expension pack") {
      return "Expansion Pack"
    } else if (type === "extentions pack" || type === "custom section" || type === "additional sections") {
      return capitalizeFirstLetter(type);
    } else if (type === "checklist") {
      return "Investor Checklist Modules"
    } else {
      return "Extra Modules";
    }
  };

  if (loading && !allCompanies.length) {
    return (
      <div className="sectionWrapper p1rem fs0-75rem pr mr2rem ml1rem pr">
        <div className="table-permission">
          <table id="table" className="table-userPermissions table-clientPermissions">
            <thead className="ps t0">
              <tr>
                <th className="index-table"></th>
                {[1, 2, 3].map(i => (
                  <th className="user-area" key={`skel-comp-${i}`}>
                    <div className="df aic">
                      <div className="mr1rem wfull">
                        <Skeleton variant="text" width={110} height={22} />
                        <Skeleton variant="text" width={60} height={16} />
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[['Core Modules', 5], ['Expansion Pack', 3]].map(([label, count], gIdx) => (
                <React.Fragment key={`skel-g-${gIdx}`}>
                  <tr>
                    <td colSpan={4} style={{ paddingTop: '40px' }}>
                      <Skeleton variant="text" width={150} height={28} sx={{ borderRadius: '4px' }} />
                    </td>
                  </tr>
                  {Array.from({ length: count }).map((_, sIdx) => (
                    <tr key={`skel-s-${gIdx}-${sIdx}`}>
                      <td style={{ background: '#fafbfe', padding: '8px 12px' }}>
                        <div className="df aic" style={{ gap: '6px' }}>
                          <Skeleton variant="rectangular" width={50} height={14} sx={{ borderRadius: '3px' }} />
                          <Skeleton variant="rectangular" width={90 + sIdx * 20} height={14} sx={{ borderRadius: '3px' }} />
                        </div>
                      </td>
                      {[1, 2, 3].map(cIdx => (
                        <td key={`skel-sc-${cIdx}`}>
                          <div className="w100 df jcsb aic bg1 p0-5rem br30px" style={{ border: '1px solid #DBDFEE' }}>
                            <Skeleton variant="circular" width={32} height={32} />
                            <Skeleton variant="circular" width={20} height={20} />
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="sectionWrapper p1rem fs0-75rem pr mr2rem ml1rem pr">
      {allCompanies && allCompanies.length > 0 ? (
        <>
          <div onClick={() => getAuditLogs(`/user/advisorPermissions`, user["companyId"]["_id"], "permissions")}
            className="df bg1 br50px mr0-5rem bw2px bss pr1rem p0-5rem bsbb aic mr1rem cp add-btn-dashboard pa t0 r0 audit-log-btn">
            <span className='bg1 bsbb mr0-5rem br100 w40px h40px tac df jcc aic'>
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M25.2239 26.0269C23.8069 27.2414 21.9654 27.975 19.9525 27.975C15.4761 27.975 11.8479 24.3468 11.8479 19.8704C11.8479 15.394 15.4761 11.7658 19.9525 11.7658C24.4289 11.7658 28.0571 15.394 28.0571 19.8704C28.0571 21.5964 27.5177 23.1964 26.5982 24.5108L29.1267 26.9914L29.2258 26.8894C29.507 26.5989 29.9772 26.592 30.2677 26.8732L34.7187 31.1929C35.0091 31.4741 35.0161 31.9421 34.7326 32.2348L33.1259 33.8898C32.8447 34.1803 32.3768 34.1872 32.084 33.906L27.6353 29.5862C27.3425 29.305 27.3356 28.8348 27.6191 28.5444L27.7028 28.4582L25.2239 26.0269ZM19.9525 13.656C16.5202 13.656 13.7381 16.4382 13.7381 19.8704C13.7381 23.3026 16.5202 26.0849 19.9525 26.0849C23.3847 26.0849 26.167 23.3026 26.167 19.8704C26.167 16.4382 23.3847 13.656 19.9525 13.656ZM19.9525 22.7712C20.3431 22.7712 20.6595 23.0876 20.6595 23.4767C20.6595 23.8673 20.3431 24.1837 19.9525 24.1837C19.562 24.1837 19.2454 23.8673 19.2454 23.4767C19.2454 23.0876 19.562 22.7712 19.9525 22.7712ZM18.9197 18.004C18.9197 18.3946 18.6032 18.7111 18.2127 18.7111C17.8221 18.7111 17.5057 18.3946 17.5057 18.004C17.5057 17.3278 17.7789 16.7165 18.2219 16.2734C18.665 15.8304 19.2764 15.5571 19.9525 15.5571C20.6287 15.5571 21.24 15.8304 21.6831 16.2734C22.1261 16.7165 22.3994 17.3278 22.3994 18.004C22.3994 18.4857 22.2605 18.9349 22.0196 19.3131C21.7726 19.7037 21.416 20.0187 20.993 20.2178C20.8865 20.2672 20.8016 20.3413 20.746 20.4308C20.6905 20.5188 20.6595 20.6268 20.6595 20.7457V21.0946C20.6595 21.4852 20.3431 21.8017 19.9525 21.8017C19.562 21.8017 19.2454 21.4852 19.2454 21.0946V20.7457C19.2454 20.3551 19.3551 19.9877 19.5526 19.6744C19.7503 19.3625 20.0359 19.1093 20.3925 18.9426C20.57 18.8577 20.7213 18.7234 20.8263 18.5567C20.9282 18.3977 20.9869 18.2078 20.9869 18.004C20.9869 17.7184 20.871 17.4606 20.6842 17.2738C20.4959 17.0854 20.2381 16.9712 19.9525 16.9712C19.6669 16.9712 19.4091 17.0854 19.2223 17.2738C19.034 17.4606 18.9197 17.7184 18.9197 18.004ZM18.7753 29.0302C17.2608 29.9784 15.5503 30.6514 13.709 30.9719L13.2649 31.0508L12.8206 30.9719C6.05857 29.7943 1.05859 23.8591 1.05859 16.9889V7.12048L13.2649 1.88672L25.474 7.12048V12.4681C24.7539 11.9301 23.9525 11.4947 23.0917 11.1836V8.96806C23.0917 8.74218 22.957 8.53797 22.7494 8.44902L13.4871 4.48034C13.3451 4.4195 13.1843 4.4195 13.0423 4.48041L3.78316 8.44902C3.57549 8.53804 3.44088 8.74218 3.44088 8.96806V17.0142C3.44088 22.7271 7.56704 27.6829 13.1592 28.7471C13.2289 28.7604 13.3005 28.7604 13.3702 28.7472C14.1727 28.5945 14.9449 28.3618 15.6791 28.0584C16.6264 28.5538 17.6704 28.8895 18.7753 29.0302ZM14.4697 27.3015C14.0779 27.4289 13.6758 27.5345 13.2649 27.617C8.25492 26.6115 4.57029 22.151 4.57029 17.0142V9.34041L13.2648 5.61378L21.9623 9.34048V10.8557C21.3153 10.7122 20.6427 10.6364 19.9525 10.6364C14.8522 10.6364 10.7185 14.7702 10.7185 19.8704C10.7185 22.9176 12.194 25.6198 14.4697 27.3015Z" fill="#4A67FF" />
              </svg>
            </span>
            <span className='c15'>Audit Log</span>
          </div>
          <div className="table-permission">
            <table id="table" className="table-userPermissions table-clientPermissions">
              <thead className="ps t0">
                <tr>
                  <th className="index-table"></th>
                  {allCompanies && allCompanies.map((company, cIndex) => {
                    return (
                      <th className="user-area" key={`${cIndex}company-${cIndex}`}>
                        <div className="df aic">
                          <div className="mr1rem wfull pr">
                            <span className="di fs1rem c0">
                              {company["cName"]}
                            </span>
                            {company["subscription"] ? (
                              <span className="c7 di ml0-5rem">({company["subscription"]?.toUpperCase()})</span>
                            ) : (<>
                              <span className="c8 di ml0-5rem">(INACTIVE)</span>
                            </>
                            )}
                            <label className="">
                              <Select
                                multiple
                                className="select-advisor w10 pa"
                                id="demo-multiple-name-1"
                                sx={{ "& .MuiSvgIcon-root": { color: "#4A67FF" } }}
                                IconComponent={ExpandMoreIcon}
                                value={[]}
                                MenuProps={{
                                  anchorOrigin: {
                                    vertical: "bottom",
                                    horizontal: "right",
                                  },
                                  transformOrigin: {
                                    vertical: "top",
                                    horizontal: "right",
                                  }
                                }}
                              >
                                {advisors.map((advisor, idx) => {
                                  return (
                                    <MenuItem
                                      key={`${cIndex}company-${cIndex}-${idx}-all_allocation`}
                                      value={advisor}
                                      className="select-advisor-list ttc">
                                      <div className={`${!hasValidAdvisor(advisor) && "disable-advisor-checkbox"}`}>
                                        <Checkbox
                                          disabled={!hasValidAdvisor(advisor)}
                                          checked={checkAdvisorHasAllAllocations({ advisors, advisor, company })}
                                          onClick={(e) => handleMultiPermissions(e, advisor, sections.filter((sec) => !sec.checklistModule).map((item) => item.sectionNo), company, sections)}
                                        />
                                      </div>
                                      <ListItemText
                                        style={{ color: !hasValidAdvisor(advisor) && "lightgray" }}
                                        primary={advisor["firstName"] + " " + advisor["lastName"] + " - " + (advisor["title"] ? advisor["title"] : advisor["designation"] ? advisor["designation"] : advisor["job"])}
                                      />
                                    </MenuItem>
                                  )
                                })}

                              </Select>
                            </label>
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {groupedSections && Object.entries(groupedSections).map(([type, typeSections], inde) => {
                  return (
                    <>
                      <tr key={`type-heading-${type}`}>
                        <td style={{ paddingTop: "40px" }} colSpan={companies.length + 1} className="type-heading">
                          <h3 style={{ borderTop: `${inde > 0 ? "1px solid rgb(219, 223, 238)" : "transparent"}`, paddingTop: "12px" }} className="c15">
                            {getSectionTitle(type.toLowerCase())}
                          </h3>
                        </td>
                      </tr>

                      {typeSections.map((section, idx2) => {
                        return (
                          <tr>
                            {/* Map Sections */}
                            <td className="c15 bg0" key={`${section["sectionName"]}${idx2}`} style={{ background: '#fafbfe' }}>
                              <span className="fw600 bg0" style={{ marginRight: "4px" }}>
                                {section["sectionNo"]}
                              </span>
                              {section['sectionName'] === "Investor Lead Introduction" ? "InvestorMatch" : section['sectionName']}
                            </td>
                            {/* Map Advisor Permission */}
                            {allCompanies &&
                              allCompanies.map((company, idxs) => {
                                return (
                                  <td className="c15 cp" key={`${idxs}-check-${idxs}-${section["sectionName"]}${idx2}`}>
                                    <div className="w100 df jcsb bg1 p0-5rem br30px" style={{ border: '1px solid #DBDFEE' }}>
                                      <div className="df">
                                        {
                                          section.sectionNo.includes("UD0") && section.type === "Additional Sections" && section.companyId !== company['_id'] ? <></> : <>
                                            {section?.["permission"]?.[company["_id"]] && Object.values(section["permission"][company["_id"]]).map((advisor, keyIndex) => {
                                              return (
                                                <>
                                                  {advisor?.["profile"] ? (
                                                    <img src={advisor["profile"]}
                                                      key={`${keyIndex}-advisor-${keyIndex}`}
                                                      className={`w3rem h3rem df jcc aic br100 mr0-5rem pr ${profileClass}`}
                                                      data-tooltip={advisor?.["firstName"] ? advisor["firstName"] + " " + advisor["lastName"] : "Register to show your name here"}
                                                    />
                                                  ) : (
                                                    <span
                                                      key={`${keyIndex}-advisor-${keyIndex + 1100000}`}
                                                      data-tooltip={advisor["firstName"] ? advisor["firstName"] + " " + advisor["lastName"] : "Register to show your name here"}
                                                      className={`w3rem h3rem df jcc aic br100 mr0-5rem pr ${profileClass}`}>
                                                      {advisor["firstName"]
                                                        ? advisor["firstName"][0] + advisor["lastName"][0] : "?"}
                                                    </span>
                                                  )}
                                                </>
                                              );
                                            })}
                                          </>
                                        }

                                      </div>
                                      <div className="pr">
                                        <Select
                                          multiple
                                          disabled={((section.sectionNo.includes("UD0") && section.type === "Additional Sections" && section.companyId !== company['_id']) || (getSectionTitle(type.toLowerCase()) === "Investor Checklist Modules" && !section?.["permission"]?.[company["_id"]]))}
                                          className="select-advisor w10"
                                          id="demo-multiple-name"
                                          sx={{ "& .MuiSvgIcon-root": { color: "#4A67FF" } }}
                                          IconComponent={ExpandMoreIcon}
                                          value={[]}
                                          MenuProps={{
                                            PaperProps: {
                                              style: {
                                                maxHeight: 300,
                                                width: 'auto',
                                                minWidth: '200px'
                                              }
                                            },
                                            anchorOrigin: {
                                              vertical: 'bottom',
                                              horizontal: 'left'
                                            },
                                            transformOrigin: {
                                              vertical: 'top',
                                              horizontal: 'left'
                                            },
                                            getContentAnchorEl: null, // Important for Safari
                                            keepMounted: true, // Helps with positioning
                                            marginThreshold: 10,
                                            style: {
                                              WebkitTransform: 'none', // Safari specific
                                              position: 'fixed' // Helps with Safari positioning
                                            }
                                          }}
                                        >
                                          {advisors.map((advisor, idxsd) => {
                                            return (
                                              <MenuItem
                                                key={`${section["sectionName"]}${idx2}--${idxsd}-drop-${idxsd}`}
                                                value={advisor._id}
                                                className="select-advisor-list ttc"
                                              >
                                                <Checkbox
                                                  checked={!!section?.["permission"]?.[company["_id"]]?.[advisor["_id"]]}
                                                  onChange={(e) => handlePermissions(e, advisor, section["sectionNo"], company)}
                                                />
                                                <ListItemText
                                                  primary={`${advisor.firstName} ${advisor.lastName} - ${advisor.title || advisor.designation || advisor.job}`}
                                                />
                                              </MenuItem>

                                            )
                                          })}
                                        </Select>
                                      </div>
                                    </div>
                                  </td>
                                );
                              })}
                          </tr>
                        );
                      })}

                    </>
                  )
                })}

              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="df jcc c15 fs1-75rem fw500 lh2 mt2rem">
          No clients added for permissions
        </div>
      )}
    </div>
  );
}
